import { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { green } from '@mui/material/colors';
import InputAdornment from '@mui/material/InputAdornment';
import { IOSSwitch } from '../common/IosSwitch';
import { useDispatch, useSelector } from 'react-redux';
import { VEHICLE_TYPE, CONDITION, BODY_STYLE, CYLINDERS, COLOR, FUEL_TYPE_MAP } from '../docs/constants';
import { selectVehicleInfo, selectLocationInfo } from '../../redux-state/selectors';
import { DECODE_VIN } from '../../redux-state/fees/types';
import { UPDATE_VEHICLE_INFO } from '../../redux-state/forms/types';
import CurrencyTextField from '../common/CurrencyField'
import { getRequiredVehicleSpecFields } from '../utility/stateRequirements';

function VehicleInfo({ errors }) {
  const [isOptionalFieldVisible, setIsOptionalFieldVisible] = useState(false);
  const vehicleInfo = useSelector(selectVehicleInfo);
  const locationInfo = useSelector(selectLocationInfo);
  const [localError, setLocalError] = useState('');
  const dispatch = useDispatch();

  // Per-state GVW / Weight / MPG requirements, using the same tables the
  // bot checks (utility/stateRequirements.js, ported from
  // state_requirements.py). Nothing here is required until a state that
  // requires it is selected, and where the ATC guide scopes the
  // requirement to particular vehicle types, only for those types.
  const requiredSpecs = getRequiredVehicleSpecFields(
    'docs',
    locationInfo?.state,
    vehicleInfo?.vehicleType
  );
  // Weight, GVW and MPG live inside the collapsed "Optional Information"
  // panel, so a state-driven requirement would otherwise be invisible —
  // and unfillable. Reveal the panel when one becomes active, or when
  // submit flags one of them. The user can still collapse it again;
  // submit-time validation is what enforces the requirement, and
  // scrollToFirstError needs the field mounted to be able to reach it,
  // which is why this reveals rather than merely flagging.
  const mustShowSpecFields =
    requiredSpecs.gvw ||
    requiredSpecs.weight ||
    requiredSpecs.mpg ||
    !!errors?.gvw ||
    !!errors?.vehicleWeight ||
    !!errors?.mpg;

  useEffect(() => {
    if (mustShowSpecFields) {
      setIsOptionalFieldVisible(true);
    }
  }, [mustShowSpecFields]);


  const validateVIN = (vin) => /^[A-HJ-NPR-Z0-9]{17}$/.test(vin.trim());

  const decodeVinCode = () => {
    if (!validateVIN(vehicleInfo.vinCode)) {
      setLocalError('Please enter a valid 17-character VIN');
      return;
    }

    setLocalError('');
    dispatch({
      type: DECODE_VIN,
      payload: { vin: vehicleInfo.vinCode } });
  };

  const handleChange = (key, value) => {
     dispatch({
        type: UPDATE_VEHICLE_INFO,
        payload: { key, value},
      });
  }

  return (
    <div className="flex flex-col w-full">
      <div className="p-4 rounded-lg bg-white text-gray-700 w-full">
        <div className="text-lg font-bold mb-2">Vehicle Info</div>
        <div className="text-gray-600 text-sm mb-4">Add Correct vehicle details</div>

        <div className="flex flex-row gap-4 w-full mb-4">
          <div className="w-full basis-[75%]">
            <TextField
              required
              label="VIN Number"
              variant="outlined"
              fullWidth
              value={vehicleInfo?.vinCode || ''}
              onChange={(e) => handleChange('vinCode', e.target.value)}
              error={!!errors?.vinCode || !!localError}
              helperText={errors?.vinCode || localError}
              InputProps={{ style: { borderRadius: '10px' } }}
            />
          </div>
          <div className="w-full basis-[25%]">
            <Button
              variant="outlined"
              fullWidth
              onClick={decodeVinCode}
              sx={{
                height: '56px',
                color: '#229954',
                borderRadius: '10px',
                borderColor: '#229954',
                '&:hover': { backgroundColor: green[800], color: '#fff' },
              }}
            >
              Decode VIN
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-4 mb-4">
          <div className="flex flex-col md:flex-row gap-4">
           <FormControl className="flex-1" required error={!!errors?.vehicleType}>
              <InputLabel>Vehicle Type</InputLabel>
              <Select
                value={vehicleInfo?.vehicleType || ''}
                onChange={(e) => handleChange('vehicleType', e.target.value)}
                label="Vehicle Type"
              >
                {Object.keys(VEHICLE_TYPE).map((key) => (
                  <MenuItem
                    key={key}
                    value={key}
                    className={key === 'Multipurpose Passenger Vehicle' ? 'hidden-important' : ''}
                  >
                    {VEHICLE_TYPE[key]}
                  </MenuItem>
              ))}
              </Select>
              {errors?.vehicleType && <div className="text-red-500 text-xs mt-1">{errors.vehicleType}</div>}
            </FormControl>

            <FormControl className="flex-1" required error={!!errors?.condition}>
              <InputLabel>Condition</InputLabel>
              <Select
                value={vehicleInfo?.condition || ''}
                onChange={(e) => handleChange('condition', e.target.value)}
                label="Condition"
              >
                {Object.values(CONDITION).map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
              {errors?.condition && <div className="text-red-500 text-xs mt-1">{errors.condition}</div>}
            </FormControl>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <TextField
              label="Model Year"
              variant="outlined"
              className="flex-1"
              value={vehicleInfo?.modelYear || ''}
              onChange={(e) => handleChange('modelYear', e.target.value)}
              InputProps={{ style: { borderRadius: '10px' } }}
            />
            <TextField
              label="Make"
              variant="outlined"
              className="flex-1"
              value={vehicleInfo?.make || ''}
              onChange={(e) => handleChange('make', e.target.value)}
              InputProps={{ style: { borderRadius: '10px' } }}
            />
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <TextField
              label="Model"
              variant="outlined"
              className="flex-1"
              value={vehicleInfo?.model || ''}
              onChange={(e) => handleChange('model', e.target.value)}
              InputProps={{ style: { borderRadius: '10px' } }}
            />
            <FormControl className="flex-1">
              <InputLabel>Body Style</InputLabel>
              <Select
                value={vehicleInfo?.bodyStyle || ''}
                onChange={(e) => handleChange('bodyStyle', e.target.value)}
                label="Body Style"
              >
                {Object.values(BODY_STYLE).map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <TextField
              label="Number of Axles"
              type="number"
              variant="outlined"
              className="flex-1"
              value={vehicleInfo?.numberOfAxles || ''}
              onChange={(e) => handleChange('numberOfAxles', e.target.value)}
              InputProps={{ style: { borderRadius: '10px' } }}
            />
            <TextField
              label="Number of Tires"
              type="number"
              variant="outlined"
              className="flex-1"
              value={vehicleInfo?.numberOfTires || ''}
              onChange={(e) => handleChange('numberOfTires', e.target.value)}
              InputProps={{ style: { borderRadius: '10px' } }}
            />
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <FormControl className="flex-1">
              <InputLabel>Fuel Type</InputLabel>
              <Select
                value={vehicleInfo?.fuelType || ''}
                onChange={(e) => handleChange('fuelType', e.target.value)}
                label="Fuel Type"
              >
              {Object.keys(FUEL_TYPE_MAP).map((key, index) => (
                <MenuItem key={index} value={key}>{FUEL_TYPE_MAP[key]}</MenuItem>
              ))}
              </Select>
            </FormControl>

            <FormControl className="flex-1">
              <InputLabel>Cylinders</InputLabel>
              <Select
                value={vehicleInfo?.cylinders || ''}
                onChange={(e) => handleChange('cylinders', e.target.value)}
                label="Cylinders"
              >
                {Object.values(CYLINDERS).map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className='flex flex-col md:flex-row gap-4'>
            <TextField
              label="Registration Months"
              type="number"
              variant="outlined"
              className="flex-1"
              value={vehicleInfo?.registrationMonths || ''}
              onChange={(e) => handleChange('registrationMonths', e.target.value)}
              InputProps={{ style: { borderRadius: '10px' } }}
            />
          </div>
        </div>

        <div className="text-gray-600 font-bold mb-4">
          Optional Information
          <FormControlLabel
            control={<IOSSwitch
            sx={{ ml: 3 }}
            checked={isOptionalFieldVisible}
            onChange={(e) => setIsOptionalFieldVisible(e.target.checked)}
          />}
          />
        </div>

        {isOptionalFieldVisible && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4 w-full">
              <TextField
                label="Odometer (miles)"
                type="number"
                variant="outlined"
                fullWidth
                value={vehicleInfo?.odometer || ''}
                onChange={(e) => handleChange('odometer', e.target.value)}
                InputProps={{ style: { borderRadius: '10px' } }}
              />
              <TextField
                label="Rim Size (inches)"
                type="number"
                variant="outlined"
                fullWidth
                value={vehicleInfo?.rimSize || ''}
                onChange={(e) => handleChange('rimSize', e.target.value)}
                InputProps={{ style: { borderRadius: '10px' } }}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-4 w-full">
              <FormControl fullWidth>
                <InputLabel>Color</InputLabel>
                <Select
                  value={vehicleInfo?.color || ''}
                  label="Color"
                  onChange={(e) => handleChange('color', e.target.value)}
                  fullWidth
                >
                  {Object.values(COLOR).map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Weight"
                type="number"
                variant="outlined"
                fullWidth
                required={requiredSpecs.weight}
                value={vehicleInfo?.vehicleWeight || ''}
                onChange={(e) => handleChange('vehicleWeight', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Select
                        value="lbs"
                        variant="standard"
                        disableUnderline
                        sx={{ fontSize: '0.875rem' }}
                      >
                        <MenuItem value="lbs">lbs</MenuItem>
                      </Select>
                    </InputAdornment>
                  ),
                  style: { borderRadius: '10px' },
                }}
                error={!!errors?.vehicleWeight}
                helperText={errors?.vehicleWeight}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-4 w-full">
              <TextField
                label="GVW (lbs)"
                variant="outlined"
                fullWidth
                required={requiredSpecs.gvw}
                value={vehicleInfo?.gvw || ''}
                onChange={(e) => handleChange('gvw', e.target.value)}
                InputProps={{ style: { borderRadius: '10px' } }}
                error={!!errors?.gvw}
                helperText={errors?.gvw}
              />
              <TextField
                label="MPG (Average)"
                type="number"
                variant="outlined"
                fullWidth
                required={requiredSpecs.mpg}
                value={vehicleInfo?.mpg || ''}
                onChange={(e) => handleChange('mpg', e.target.value)}
                InputProps={{ style: { borderRadius: '10px' } }}
                error={!!errors?.mpg}
                helperText={errors?.mpg}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-4 w-full">
              <TextField
                label="Horsepower"
                variant="outlined"
                fullWidth
                value={vehicleInfo?.horsepower || ''}
                onChange={(e) => handleChange('horsepower', e.target.value)}
                InputProps={{ style: { borderRadius: '10px' } }}
              />
              <CurrencyTextField
                label="MSRP"
                name="msrp"
                required
                value={vehicleInfo.msrp ?? ''} // keep controlled
                onChange={(num) =>
                  dispatch({
                    type: 'UPDATE_VEHICLE_INFO',
                    payload: { key: 'msrp', value: num }, // <-- num is a Number or '' when cleared
                  })
                }
                InputProps={{ style: { borderRadius: '10px' } }}
                error={!!errors?.msrp}
                helperText={errors?.msrp}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VehicleInfo;
